import { useMemo, useRef, useState, type CSSProperties } from 'react';
import type { JSONContent } from '@tiptap/core';
import {
  QUESTION_TRANSLATABLE_FIELDS,
  useTheme,
  type QuestionDefinitionDto,
  type QuestionTranslationField,
  type QuestionTranslationFields,
} from '@course-authoring/shared-types';

import { InteractionRichTextField } from '../InteractionRichTextField';
import { createTokenRichTextToolbarStyles } from '../interactionEditorUtils';

type LabelMapField = 'options' | 'categories' | 'items';

/**
 * Label-map fields rendered as id-keyed text inputs. Which of these actually
 * appear is decided by QUESTION_TRANSLATABLE_FIELDS for the question's type, so
 * adding a translatable label collection is a registry change plus one entry
 * here rather than another conditional block.
 */
const LABEL_MAP_GROUPS: ReadonlyArray<{
  field: LabelMapField;
  title: string;
  children: (source: QuestionDefinitionDto) => ReadonlyArray<{ id: number; label: string }>;
}> = [
  { field: 'options', title: 'Answer options', children: (source) => source.answerOptions },
  { field: 'categories', title: 'Categories', children: (source) => source.categories },
  { field: 'items', title: 'Items', children: (source) => source.items },
];

interface QuestionTranslationFormProps {
  englishSource: QuestionDefinitionDto;
  fields: QuestionTranslationFields;
  onChange: (fields: QuestionTranslationFields) => void;
  errors: string[];
}

function RichTextTranslationField({
  label,
  content,
  onChange,
  autoFocus = false,
}: {
  label: string;
  content: JSONContent;
  onChange: (content: JSONContent) => void;
  autoFocus?: boolean;
}) {
  const { tokens } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const toolbarStyles = useMemo(() => createTokenRichTextToolbarStyles(tokens), [tokens]);
  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing.xs }}>
      <strong style={{ color: tokens.text.primary, fontSize: '13px' }}>{label}</strong>
      <div style={{ border: `1px solid ${tokens.borders.default}`, borderRadius: tokens.radius.sm, padding: tokens.spacing.sm }}>
        <InteractionRichTextField
          content={content}
          placeholder={`Enter ${label.toLowerCase()}…`}
          isFocused={focused}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onContentChange={onChange}
          autoFocus={autoFocus}
          containerRef={containerRef}
          toolbarPreset="full"
          toolbarStyles={toolbarStyles}
        />
      </div>
    </div>
  );
}

export function QuestionTranslationForm({
  englishSource,
  fields,
  onChange,
  errors,
}: QuestionTranslationFormProps) {
  const { tokens } = useTheme();
  const styles = useMemo((): Record<string, CSSProperties> => ({
    section: { display: 'flex', flexDirection: 'column', gap: tokens.spacing.md },
    groupTitle: { color: tokens.text.primary, fontSize: '13px', margin: 0 },
    input: {
      width: '100%',
      boxSizing: 'border-box',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
      backgroundColor: tokens.backgrounds.page,
      color: tokens.text.primary,
    },
    row: { display: 'flex', flexDirection: 'column', gap: tokens.spacing.xs },
    error: { margin: 0, color: tokens.ui.errorText, fontSize: '13px' },
  }), [tokens]);

  const setLabel = (field: LabelMapField, id: number, label: string) => {
    onChange({ ...fields, [field]: { ...fields[field], [String(id)]: label } });
  };
  const explanation = fields.correctAnswerExplanation;

  // Which fields this type may translate comes from the shared registry, so the
  // form cannot drift from what the API accepts.
  const translatable = new Set<QuestionTranslationField>(
    QUESTION_TRANSLATABLE_FIELDS[englishSource.blockType],
  );
  // The explanation is offered only when English actually has one to translate.
  const englishExplanation = englishSource.answerOptions
    .find((option) => option.isTarget)?.correctAnswerExplanation;
  const showExplanation = translatable.has('correctAnswerExplanation')
    && (englishSource.blockType !== 'single-choice-answer' || englishSource.gated)
    && Boolean(explanation || englishExplanation);

  return (
    <div style={styles.section}>
      {errors.map((error) => <p key={error} role="alert" style={styles.error}>{error}</p>)}

      <RichTextTranslationField
        label="Question text"
        content={fields.displayText ?? englishSource.questionText}
        onChange={(displayText) => onChange({ ...fields, displayText })}
        autoFocus
      />

      {LABEL_MAP_GROUPS.filter(({ field }) => translatable.has(field)).map(({ field, title, children }) => (
        <div key={field} style={styles.section}>
          <strong style={styles.groupTitle}>{title}</strong>
          {children(englishSource).map((child) => (
            <div key={child.id} style={styles.row}>
              <input
                style={styles.input}
                // No visible English hint by request; kept as the accessible name
                // so the input is still identifiable to assistive tech.
                aria-label={child.label}
                value={fields[field]?.[String(child.id)] ?? child.label}
                onChange={(event) => setLabel(field, child.id, event.target.value)}
              />
            </div>
          ))}
        </div>
      ))}

      {showExplanation && (
        <RichTextTranslationField
          label="Correct answer explanation"
          content={explanation ?? englishExplanation ?? { type: 'doc' }}
          onChange={(correctAnswerExplanation) => onChange({ ...fields, correctAnswerExplanation })}
        />
      )}
    </div>
  );
}
